import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import EditableImage from "../../landingEditor/EditableImage";
import { getValueAtDescriptorPath } from "../elementEditing";

const GROUPS = Object.freeze({
  distribution: ["gap", "radius", "imagePosition", "imageFit", "imageHeight"],
  visibility: [
    "showImage", "showTitle", "showType", "showCategory", "showSubcategory",
    "showDescription", "showPrice", "showDuration", "showCapabilities", "showQuickCart", "showButton",
  ],
  background: ["backgroundColor", "backgroundOpacity", "backgroundImageUrl", "backgroundImagePosition", "backgroundImageFit", "backgroundImageOpacity"],
  copy: [
    "typeColor", "titleColor", "categoryColor", "subcategoryColor", "descriptionColor", "priceColor",
    "buttonTextColor", "buttonBackgroundColor", "buttonHoverTextColor", "buttonHoverBackgroundColor",
    "ctaLabel", "serviceCtaLabel", "ctaBehavior", "ctaUrl",
  ],
  borderShadow: ["borderColor", "shadowColor", "shadowOpacity", "shadowX", "shadowY", "shadowBlur", "shadowSpread"],
});

const DISPLAY_LABELS = Object.freeze({
  showImage: "Imagen",
  showTitle: "Título",
  showType: "Tipo",
  showCategory: "Categoría",
  showSubcategory: "Subcategoría",
  showDescription: "Descripción",
  showPrice: "Precio",
  showDuration: "Duración",
  showCapabilities: "Reserva / entrega",
  showQuickCart: "Compra rápida",
  showButton: "Botón",
});

function safeHex(value, fallback = "#000000") {
  const text = String(value || "").trim();
  return /^#[0-9a-f]{6}$/i.test(text) ? text : fallback;
}

function alphaColor(value, opacity, fallback) {
  const color = String(value || fallback || "transparent").trim() || "transparent";
  const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(opacity)) ? Number(opacity) : 1));
  if (alpha <= 0) return "transparent";
  if (alpha >= 0.999) return color;
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function fieldMap(descriptor) {
  return new Map((descriptor?.fields || []).map((field) => [String(field.path || "").split(".").pop(), field]));
}

function valueFor(form, field, fallback = "") {
  return field ? getValueAtDescriptorPath(form, field.path, field.defaultValue ?? fallback) : fallback;
}

function NumberControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  const value = valueFor(form, field, "");
  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={field.label}
      value={value ?? ""}
      disabled={disabled}
      inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }}
      onChange={(event) => {
        if (event.target.value === "") {
          onCommit(field.path, "");
          return;
        }
        const next = Number(event.target.value);
        if (Number.isFinite(next)) onCommit(field.path, next);
      }}
    />
  );
}

function SelectControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>{field.label}</InputLabel>
      <Select label={field.label} value={valueFor(form, field, "") ?? ""} onChange={(event) => onCommit(field.path, event.target.value)}>
        {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function TextControl({ field, form, disabled, onCommit, helperText = "" }) {
  if (!field) return null;
  return (
    <TextField
      fullWidth
      size="small"
      label={field.label}
      value={valueFor(form, field, "") ?? ""}
      disabled={disabled}
      inputProps={{ maxLength: field.maxLength }}
      helperText={helperText || undefined}
      onChange={(event) => onCommit(field.path, event.target.value)}
    />
  );
}

function ColorControl({ field, form, disabled, onCommit, fallback = "#000000" }) {
  if (!field) return null;
  const own = String(valueFor(form, field, "") || "").trim();
  const inherited = !own;
  const transparent = own.toLowerCase() === "transparent";
  const stateLabel = inherited ? "Heredado" : transparent ? "Transparente" : "Personalizado";
  return (
    <Stack spacing={0.75} sx={{ p: 1, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="body2" sx={{ fontWeight: 850 }}>{field.label}</Typography>
        <Chip size="small" label={stateLabel} variant={inherited ? "outlined" : "filled"} />
      </Stack>
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Box
          component="input"
          type="color"
          aria-label={field.label}
          value={safeHex(own, safeHex(fallback))}
          disabled={disabled}
          onChange={(event) => onCommit(field.path, event.target.value)}
          sx={{ width: 42, height: 36, p: 0.2, border: "1px solid rgba(25,36,47,.14)", borderRadius: 1.5, bgcolor: "transparent" }}
        />
        <TextField
          key={`${field.path}:${own}`}
          size="small"
          defaultValue={own}
          disabled={disabled}
          placeholder={fallback || "Heredado"}
          inputProps={{ "aria-label": `${field.label} CSS`, maxLength: field.maxLength }}
          onFocus={(event) => event.target.select()}
          onBlur={(event) => {
            const next = String(event.target.value || "").trim();
            if (next !== own) onCommit(field.path, next);
          }}
          sx={{ flex: 1 }}
        />
      </Stack>
      <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap">
        {!inherited ? <Button size="small" disabled={disabled} onClick={() => onCommit(field.path, "")} sx={{ textTransform: "none" }}>Heredar</Button> : null}
        {!transparent ? <Button size="small" disabled={disabled} onClick={() => onCommit(field.path, "transparent")} sx={{ textTransform: "none" }}>Transparente</Button> : null}
      </Stack>
    </Stack>
  );
}

function ShadowPreview({ values, hover }) {
  const shadowOpacity = values.shadowOpacity === "" || values.shadowOpacity === undefined ? 1 : Number(values.shadowOpacity);
  const shadowColor = alphaColor(values.shadowColor || "#000000", shadowOpacity, "#000000");
  const shadow = shadowOpacity <= 0
    ? "none"
    : `${Number(values.shadowX ?? 0) || 0}px ${Number(values.shadowY ?? 8) || 0}px ${Number(values.shadowBlur ?? 24) || 0}px ${Number(values.shadowSpread ?? 0) || 0}px ${shadowColor}`;
  const background = alphaColor(values.backgroundColor || "#ffffff", values.backgroundOpacity ?? 1, "#ffffff");
  const imagePosition = values.imagePosition || "top";
  const horizontal = imagePosition === "left" || imagePosition === "right";
  const buttonColor = hover ? (values.buttonHoverTextColor || values.buttonTextColor || "#fff") : (values.buttonTextColor || "#fff");
  const buttonBackground = hover ? (values.buttonHoverBackgroundColor || values.buttonBackgroundColor || "#6a5ae0") : (values.buttonBackgroundColor || "#6a5ae0");
  const imageBg = values.backgroundImageUrl ? `url("${String(values.backgroundImageUrl).replace(/"/g, "%22")}")` : "none";

  return (
    <Box sx={{ p: 2.2, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.035)", overflow: "hidden" }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: horizontal ? (imagePosition === "right" ? "row-reverse" : "row") : "column",
          maxWidth: 300,
          mx: "auto",
          borderRadius: `${Number(values.radius ?? 16) || 0}px`,
          border: `1px solid ${values.borderColor || "rgba(25,36,47,.14)"}`,
          bgcolor: background,
          boxShadow: shadow,
          transform: hover ? "translateY(-2px)" : "none",
          transition: "all .16s ease",
          isolation: "isolate",
          "&::before": values.backgroundImageUrl ? {
            content: '\"\"',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: imageBg,
            backgroundSize: values.backgroundImageFit === "contain" ? "contain" : "cover",
            backgroundPosition: values.backgroundImagePosition || "center",
            backgroundRepeat: "no-repeat",
            opacity: Number(values.backgroundImageOpacity ?? .28),
          } : undefined,
          "& > *": { position: "relative", zIndex: 1 },
        }}
      >
        {values.showImage !== false ? (
          <Box sx={{ width: horizontal ? 82 : "100%", minWidth: horizontal ? 82 : 0, height: horizontal ? "auto" : Math.min(150, Number(values.imageHeight ?? 210) || 210), bgcolor: "rgba(106,90,224,.14)", background: "linear-gradient(135deg, rgba(106,90,224,.18), rgba(25,36,47,.08))" }} />
        ) : null}
        <Stack spacing={0.45} sx={{ p: 1.4, flex: 1, minWidth: 0 }}>
          {values.showType !== false ? <Typography variant="caption" sx={{ color: values.typeColor || "primary.main", fontWeight: 850 }}>Producto</Typography> : null}
          {values.showTitle !== false ? <Typography sx={{ color: values.titleColor || "text.primary", fontWeight: 900, fontSize: ".92rem" }}>Producto de ejemplo</Typography> : null}
          {values.showCategory !== false ? <Typography variant="caption" sx={{ color: values.categoryColor || "text.secondary" }}>Categoría</Typography> : null}
          {values.showSubcategory === true ? <Typography variant="caption" sx={{ color: values.subcategoryColor || "text.secondary" }}>Subcategoría</Typography> : null}
          {values.showDescription !== false ? <Typography variant="caption" sx={{ color: values.descriptionColor || "text.secondary" }}>Descripción breve de ejemplo.</Typography> : null}
          {values.showDuration !== false ? <Typography variant="caption" sx={{ color: values.descriptionColor || "text.secondary" }}>Duración: 60 min</Typography> : null}
          {values.showCapabilities !== false ? <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap"><Chip size="small" label="Reserva" /><Chip size="small" label="Entrega" /></Stack> : null}
          {values.showPrice !== false ? <Typography sx={{ color: values.priceColor || "text.primary", fontWeight: 950 }}>$ 12.500</Typography> : null}
          {values.showQuickCart !== false ? <Chip size="small" icon={<ShoppingCartOutlinedIcon />} label="Compra rápida" sx={{ alignSelf: "flex-start" }} /> : null}
          {values.showButton !== false ? (
            <Button size="small" variant="contained" sx={{ mt: .4, alignSelf: "flex-start", textTransform: "none", color: buttonColor || undefined, bgcolor: buttonBackground || undefined, "&:hover": { bgcolor: buttonBackground || undefined } }}>
              {values.ctaLabel || "Ver detalle"}
            </Button>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

function EditorGroup({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&::before": { display: "none" }, overflow: "hidden" }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 52, "& .MuiAccordionSummary-content": { my: 1 } }}>
        <Stack spacing={0.15}>
          <Typography variant="body2" sx={{ fontWeight: 950 }}>{title}</Typography>
          {subtitle ? <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>{subtitle}</Typography> : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}><Stack spacing={1.2}>{children}</Stack></AccordionDetails>
    </Accordion>
  );
}

export default function CardSystemEditor({ descriptor, form, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const fields = React.useMemo(() => fieldMap(descriptor), [descriptor]);
  const [previewState, setPreviewState] = React.useState("normal");
  const field = (suffix) => fields.get(suffix) || null;
  const read = (suffix, fallback) => valueFor(form, field(suffix), fallback);
  const values = React.useMemo(() => {
    const output = {};
    [...GROUPS.distribution, ...GROUPS.visibility, ...GROUPS.background, ...GROUPS.copy, ...GROUPS.borderShadow].forEach((suffix) => {
      output[suffix] = read(suffix, undefined);
    });
    return output;
  }, [form, fields]);

  const backgroundImageField = field("backgroundImageUrl");
  const behavior = read("ctaBehavior", "detail");

  return (
    <Stack spacing={1.3}>
      <Alert severity="info" icon={false} sx={{ py: .6 }}>
        Estos cambios se aplican a todas las cards.
      </Alert>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Preview</Typography>
        <ToggleButtonGroup exclusive size="small" value={previewState} onChange={(_, value) => value && setPreviewState(value)} aria-label="Estado del preview de la card">
          <ToggleButton value="normal" aria-label="Vista normal">Normal</ToggleButton>
          <ToggleButton value="hover" aria-label="Vista hover">Hover</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <ShadowPreview values={values} hover={previewState === "hover"} />

      <EditorGroup title="Distribución" subtitle="Forma, separación e imagen" defaultExpanded>
        <NumberControl field={field("gap")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("radius")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("imagePosition")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("imageFit")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("imageHeight")} form={form} disabled={disabled} onCommit={onCommit} />
      </EditorGroup>

      <EditorGroup title="Qué mostrar" subtitle="Activá sólo la información útil">
        <Stack direction="row" spacing={.65} useFlexGap flexWrap="wrap">
          {GROUPS.visibility.map((suffix) => {
            const currentField = field(suffix);
            if (!currentField) return null;
            const checked = read(suffix, true) !== false;
            return (
              <Chip
                key={suffix}
                size="small"
                clickable={!disabled}
                label={DISPLAY_LABELS[suffix] || currentField.label}
                variant={checked ? "filled" : "outlined"}
                onClick={disabled ? undefined : () => onCommit(currentField.path, !checked)}
                aria-pressed={checked}
              />
            );
          })}
        </Stack>
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
          Activo = visible. Tocá cada chip para mostrar u ocultar ese dato.
        </Typography>
      </EditorGroup>

      <EditorGroup title="Fondo e imagen" subtitle="Color, transparencia e imagen de fondo">
        <ColorControl field={field("backgroundColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#ffffff" />
        <NumberControl field={field("backgroundOpacity")} form={form} disabled={disabled} onCommit={onCommit} />
        {backgroundImageField ? (
          <EditableImage
            value={read("backgroundImageUrl", "")}
            label={backgroundImageField.label || "Imagen de fondo"}
            disabled={disabled}
            height={130}
            onUpload={(file, metadata) => onUploadImage?.(backgroundImageField.path, file, metadata)}
            onRemove={() => onRemoveImage?.(backgroundImageField.path)}
          />
        ) : null}
        <SelectControl field={field("backgroundImagePosition")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("backgroundImageFit")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("backgroundImageOpacity")} form={form} disabled={disabled} onCommit={onCommit} />
      </EditorGroup>

      <EditorGroup title="Textos y botón" subtitle="Colores, copies y destino de acción">
        <ColorControl field={field("typeColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#6a5ae0" />
        <ColorControl field={field("titleColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#19242F" />
        <ColorControl field={field("categoryColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#697386" />
        <ColorControl field={field("subcategoryColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#697386" />
        <ColorControl field={field("descriptionColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#697386" />
        <ColorControl field={field("priceColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#19242F" />
        <ColorControl field={field("buttonTextColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#ffffff" />
        <ColorControl field={field("buttonBackgroundColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#6a5ae0" />
        <ColorControl field={field("buttonHoverTextColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#ffffff" />
        <ColorControl field={field("buttonHoverBackgroundColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#5848cf" />
        <TextControl field={field("ctaLabel")} form={form} disabled={disabled} onCommit={onCommit} />
        <TextControl field={field("serviceCtaLabel")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("ctaBehavior")} form={form} disabled={disabled} onCommit={onCommit} />
        {behavior === "custom" ? <TextControl field={field("ctaUrl")} form={form} disabled={disabled} onCommit={onCommit} helperText="Podés usar {id} y {type} en el link." /> : null}
      </EditorGroup>

      <EditorGroup title="Borde y sombra" subtitle="Opciones avanzadas">
        <ColorControl field={field("borderColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#E1E4EA" />
        <ColorControl field={field("shadowColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#000000" />
        <NumberControl field={field("shadowOpacity")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowX")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowY")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowBlur")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowSpread")} form={form} disabled={disabled} onCommit={onCommit} />
      </EditorGroup>
    </Stack>
  );
}
